import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { firstValueFrom, filter, tap } from 'rxjs';
import { getSdlc } from '@mhe/reader/core/state/configuration/configuration.selectors';
import { Env } from '@mhe/reader/types';

const ALLOWED_ENVIRONMENTS: Env[] = ['dev', 'qastg', 'qalv'];
const LOCALSTORAGE_KEY = 'CAT_WEBCOMPONENT_URL';

/**
 * Validates if a string is a valid URL.
 * @param url The URL string to validate
 * @returns true if valid URL, false otherwise
 */
function isValidUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Service that injects a web component script into the HTML head
 * for development and lower environments only.
 *
 * Reads the script URL from localStorage and dynamically creates
 * a <script defer> tag in the document head.
 */
@Injectable({
  providedIn: 'root',
})
export class CatWebcomponentLoaderService {
  private _currentEnv: string | null = null;

  constructor(
    @Inject(WINDOW) readonly windowRef: Window,
    @Inject(DOCUMENT) readonly documentRef: Document,
  ) {}

  set currentEnv(env: string) {
    this._currentEnv = env;
  }

  get currentEnv(): string | null {
    return this._currentEnv;
  }

  /**
   * Checks if a script with the given URL already exists in the document head.
   * @param url The script URL to check
   * @returns true if script already exists, false otherwise
   */
  private scriptExists(url: string): boolean {
    const scripts = this.documentRef.head.querySelectorAll('script');
    return Array.from(scripts).some(script => script.src === url);
  }

  /**
   * Injects a script tag into the document head with the provided URL.
   * Includes duplicate prevention and error handling.
   * @param url The URL of the script to inject
   */
  injectScript(url: string): void {
    if (!url || url.trim() === '') {
      console.warn('[CatWebcomponentLoader] No URL provided, skipping script injection');
      return;
    }

    // Check for duplicate script
    if (this.scriptExists(url)) {
      console.log('[CatWebcomponentLoader] Script already exists in head, skipping:', url);
      return;
    }

    console.log('[CatWebcomponentLoader] Injecting web component script:', url);

    const scriptTag = this.documentRef.createElement('script');
    scriptTag.src = url;
    scriptTag.type = 'text/javascript';
    scriptTag.defer = true;
    scriptTag.dataset.env = this._currentEnv || '';

    // Add error handler for failed script loads
    scriptTag.onerror = (error) => {
      console.error('[CatWebcomponentLoader] Failed to load web component script:', url, error);
    };

    // Append to head
    this.documentRef.head.appendChild(scriptTag);
  }
}

/**
 * Factory function for APP_INITIALIZER that checks environment and localStorage
 * before injecting the web component script.
 *
 * Only injects the script if:
 * 1. Environment is dev, qastg, or qalv
 * 2. localStorage contains a valid URL for CAT_WEBCOMPONENT_URL
 *
 * @param store NgRx Store to access environment configuration
 * @param service CatWebcomponentLoaderService instance
 * @param windowRef Window reference for localStorage access
 * @returns Factory function returning a Promise<void>
 */
export function catWebComponentFactory(
  store: Store,
  service: CatWebcomponentLoaderService,
  windowRef: Window,
): () => Promise<void> {
  return async() => {
    try {
      // Get SDLC environment from store (retry until value exists)
      const sdlc = await firstValueFrom(
        store.select(getSdlc)
          .pipe(
            filter(val => !!val && val !== ''),
            tap(val => {
              service.currentEnv = val;
            }),
          ),
      );

      console.log('[CatWebcomponentLoader] Current environment:', sdlc);

      // Check if environment is allowed
      if (!ALLOWED_ENVIRONMENTS.includes(sdlc as Env)) {
        console.log('[CatWebcomponentLoader] Environment not allowed:', sdlc);
        return;
      }

      // Read URL from localStorage
      const webComponentUrl = windowRef.localStorage.getItem(LOCALSTORAGE_KEY);

      if (!webComponentUrl || webComponentUrl.trim() === '') {
        console.log('[CatWebcomponentLoader] No URL found in localStorage');
        return;
      }

      // Validate URL structure
      if (!isValidUrl(webComponentUrl)) {
        console.warn('[CatWebcomponentLoader] Invalid URL structure:', webComponentUrl);
        return;
      }

      // Inject the script
      service.injectScript(webComponentUrl);
    } catch (error) {
      console.error('[CatWebcomponentLoader] Error during initialization:', error);
    }
  };
}
